import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wrench, Cog, Package, ShoppingBag, Bike, Truck, ArrowRight, Check } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { seoPageList } from "../data/seoPages";

const diensten = [
  { icon: Wrench, title: "Onderhoud", anchor: "onderhoud", img: "/onderhoud-tt.jpg", imgPos: "object-center",
    alt: "Husqvarna Norden 901 op de motorbrug voor onderhoud in de werkplaats van TT Motorsports Torhout",
    text: "Regelmatig onderhoud houdt je motor betrouwbaar en veilig. Wij voeren periodiek onderhoud uit volgens de fabrieksvoorschriften: olie, filters, kettingen, remmen en meer.",
    points: ["Grote en kleine beurten", "Olie- en filterservice", "Ketting & tandwielen", "Remmen & vloeistoffen"] },
  { icon: Cog, title: "Herstellingen", anchor: "herstellingen", img: "/herstellingen-tt.jpg", imgAspect: "aspect-[3/4]", imgFit: "object-cover", imgPos: "object-center",
    alt: "Gedemonteerde motor op de standaard tijdens een herstelling in de werkplaats van TT Motorsports Torhout",
    text: "Van een klein defect tot een volledige revisie — onze technici herstellen motoren van alle merken snel en vakkundig, met kwalitatieve onderdelen.",
    points: ["Motorblok revisie", "Ophanging & vering", "Elektronica", "Bandenwissel & balanceren"] },
  { icon: Package, title: "Onderdelen", anchor: "onderdelen", img: "/onderdelen-tt.jpg", imgAspect: "aspect-[3/4]", imgFit: "object-cover", imgPos: "object-center",
    to: "/onderdelen", ctaLabel: "Bekijk onderdelen",
    alt: "GASGAS crossmotor met Hinson-koppeling en performance-onderdelen bij TT Motorsports Torhout",
    text: "Originele en aftermarket onderdelen voor nagenoeg elk merk. Snel besteld, correct gemonteerd en met advies op maat.",
    points: ["Originele onderdelen", "Aftermarket", "Snelle levering", "Montage inbegrepen"] },
  { icon: ShoppingBag, title: "Accessoires", anchor: "accessoires", img: "/accessoires-tt.jpg", imgAspect: "aspect-[3/4]", imgFit: "object-cover", imgPos: "object-center",
    to: "/contact", ctaLabel: "Vraag info",
    alt: "Accessoires en toebehoren voor motor en scooter bij TT Motorsports Torhout",
    text: "Handige accessoires en toebehoren om je motor of scooter compleet te maken. Van bagageoplossingen en beveiliging tot onderhoudsproducten en styling — wij adviseren je graag en monteren alles vakkundig in eigen werkplaats.",
    points: ["Koffers & bagagedragers", "Sloten & beveiliging", "Onderhoudsproducten", "Verlichting & styling"] },
  { icon: Bike, title: "Verkoop van motoren", anchor: "verkoop", img: "/motoren-verkoop-tt.jpg", imgAspect: "aspect-[3/4]", imgFit: "object-cover", imgPos: "object-center",
    to: "/motoren", ctaLabel: "Bekijk motoren",
    alt: "Rij nieuwe Santini scooters en motoren in de toonzaal van TT Motorsports Torhout",
    text: "Nieuwe en zorgvuldig geselecteerde tweedehands motoren. Elke occasie wordt in eigen werkplaats gecontroleerd en klaargemaakt voor de baan.",
    points: ["Nieuwe motoren", "Gekeurde occasies", "Inruil mogelijk", "Persoonlijk advies"] },
  { icon: Truck, title: "Ophalen & leveren", anchor: "ophalen-leveren", img: "/transport-tt-v2.jpg", imgAspect: "aspect-[3/4]", imgFit: "object-cover", imgPos: "object-center",
    to: "/contact", ctaLabel: "Plan een ophaling",
    alt: "Bestelwagens van TT Motorsports met logo, klaar om motoren op te halen en te leveren in heel België",
    text: "Geen vervoer voor je motor? Geen probleem. TT Motorsports haalt je motor op en levert ze terug — in heel België. Zo hoef jij je nergens zorgen over te maken: wij regelen het veilige transport van en naar onze werkplaats in Torhout, voor onderhoud, herstelling of een aankoop.",
    points: ["Ophalen aan huis", "Levering in heel België", "Veilig & verzekerd transport", "Handig bij onderhoud of aankoop"] },
];

export default function Diensten() {
  useSEO({
    title: "Diensten TT Motorsports Torhout — onderhoud, herstelling & onderdelen",
    description: "Motoronderhoud, motorherstellingen, diagnose, motoronderdelen, accessoires en verkoop van motoren bij TT Motorsports in Torhout. Voor alle merken en alle types motoren: cross, enduro, baan, sport en touring.",
    keywords: "motoronderhoud, motor herstelling, motor reparatie, diagnose motor, motoronderdelen, motor accessoires, crossmotor onderhoud, enduro onderhoud, baanmotor onderhoud, TT Motorsports Torhout",
  });

  return (
    <div data-testid="diensten-page">
      <section className="relative pt-36 pb-16 bg-[#0A0A0B] border-b border-[#2A2B31] overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full racing-stripe opacity-[0.04]" />
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative">
          <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Wat we doen</span>
          <h1 className="heading-xl text-5xl md:text-7xl text-white mt-3">Onze diensten</h1>
          <p className="text-[#9A9CA3] text-lg mt-5 max-w-2xl">Alles voor je motor onder één dak — voor <span className="text-white">alle merken en alle types motoren</span>, van crossmotor tot sport- en touringmotor. Technische expertise, kwalitatieve onderdelen en persoonlijke service.</p>
        </div>
      </section>

      <section className="bg-[#0A0A0B]">
        {diensten.map((d, i) => (
          <motion.div
            key={d.title}
            id={d.anchor}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            data-testid={`dienst-block-${i}`}
            className={`scroll-mt-28 border-b border-[#2A2B31] ${i % 2 === 1 ? "bg-[#0D0D0F]" : ""}`}
          >
            <div className={`max-w-[1400px] mx-auto px-5 md:px-8 py-16 grid md:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
              <div className="[direction:ltr]">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#E10600] flex items-center justify-center">
                    <d.icon size={24} className="text-white" strokeWidth={1.6} />
                  </div>
                  <span className="font-mono-tt text-sm text-[#5A5C63]">0{i + 1}</span>
                </div>
                <h2 className="heading-xl text-3xl md:text-5xl text-white mb-4">{d.title}</h2>
                <p className="text-[#B8B9BE] leading-relaxed mb-6">{d.text}</p>
                <ul className="grid grid-cols-2 gap-3 mb-8">
                  {d.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-[#E6E6E8]">
                      <Check size={16} className="text-[#E10600] shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
                <Link to={d.to || "/contact"} className="inline-flex items-center gap-2 text-[#E10600] hover:text-white font-display uppercase font-700 text-sm tracking-wide transition-colors">
                  {d.ctaLabel || "Vraag info"} <ArrowRight size={16} />
                </Link>
              </div>
              <div className={`[direction:ltr] ${d.imgAspect || "aspect-[4/3]"} overflow-hidden border border-[#2A2B31] group bg-[#0A0A0B]`}>
                <img src={d.img} alt={d.alt || d.title} loading="lazy" className={`w-full h-full ${d.imgFit || "object-cover"} ${d.imgPos || "object-center"} card-hover-img`} />
              </div>
            </div>
          </motion.div>
        ))}
      </section>

      {/* SEO detailpagina's */}
      <section className="section-pad bg-[#0D0D0F] border-t border-[#2A2B31]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Meer weten?</span>
          <h2 className="heading-xl text-3xl md:text-5xl text-white mt-3 mb-4">Onze diensten in detail</h2>
          <p className="text-[#9A9CA3] text-lg max-w-2xl mb-10">Lees meer over ons motoronderhoud, herstellingen en onderdelen voor alle merken en types motoren in Torhout en de regio West-Vlaanderen.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {seoPageList.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`} data-testid={`diensten-seo-link-${p.slug}`}
                className="group bg-[#0A0A0B] border border-[#2A2B31] hover:border-[#E10600]/60 p-5 transition-colors">
                <p className="font-display uppercase font-700 text-white group-hover:text-[#E10600] transition-colors leading-tight">{p.title}</p>
                <span className="text-[#9A9CA3] text-sm flex items-center gap-1 mt-2">Lees meer <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
